import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    CheckCircle2, XCircle, Loader2, ArrowRight, Phone, Mail,
    FileText, MessageCircle, ArrowLeft, FileDown, HelpCircle,
    Home, Camera, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import PageHead from "@/components/PageHead";
import {
    CARD, LABEL, H2, ClientIntro, ClientHero, ClientNav, OverviewCards, ContactsPanel,
} from "@/components/client/ClientAreaParts";
import { ConversationsPanel } from "@/components/client/ClientConversations";

const MONTHS_PT = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function formatLongDatePT(value) {
    const [y, m, d] = String(value || "").slice(0, 10).split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return "";
    return `${d} de ${MONTHS_PT[m - 1]} de ${y}`;
}

function formatDateTimePT(iso) {
    if (!iso) return "";
    try {
        const dt = new Date(iso);
        return dt.toLocaleString("pt-PT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

/**
 * Área do Cliente — página pública (link privado). Topo com estado da obra,
 * navegação por secções (tabs simples, sem routing) e conteúdo existente.
 */
export default function ClientArea() {
    const { token } = useParams();
    const settingsCtx = useSettings();
    const settings = settingsCtx?.settings || {};
    const [state, setState] = useState({ status: "loading", data: null });
    const [tab, setTab] = useState("overview");
    const [questionOpen, setQuestionOpen] = useState(false);
    const contentRef = useRef(null);

    const reload = useCallback(async () => {
        if (!token) { setState({ status: "error", data: null }); return; }
        try {
            const r = await api.get(`/public/cliente/${token}`);
            setState({ status: "ok", data: r.data });
        } catch {
            setState({ status: "error", data: null });
        }
    }, [token]);

    useEffect(() => { reload(); }, [reload]);

    if (state.status === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--cream))]" data-testid="client-loading">
                <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
        );
    }

    if (state.status === "error") {
        return (
            <>
                <PageHead title="Link inválido — O Homem da Obra" description="" canonical={null} />
                <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--cream))] p-6" data-testid="client-error">
                    <div className="max-w-md text-center">
                        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-6" />
                        <h1 className="font-display text-3xl font-bold text-stone-900 mb-3">Link não disponível</h1>
                        <p className="text-stone-600 mb-8">Este link não é válido ou já não está disponível.</p>
                        <Link to="/" className="inline-flex items-center gap-2 text-[hsl(var(--copper))] hover:underline text-sm font-medium">
                            Visitar ohomemdaobra.pt <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    const d = state.data;
    const p = d.proposal || {};
    const updates = Array.isArray(d.execution?.updates) ? d.execution.updates : [];
    const hasUpdates = d.status === "Execução" || d.status === "Concluído";
    const questionsOn = !!p.available || ["Adjudicado", "Execução", "Concluído"].includes(d.status);
    const docsCount = p.files ? Object.values(p.files).filter(Boolean).length : 0;
    const openQ = d.open_questions || 0;
    const unreadQ = d.unread_questions || 0;
    const conversations = Array.isArray(d.questions) ? d.questions : [];
    const heroPhoto = d.cover_file_id || null;

    const go = (key) => {
        setTab(key);
        if (window.innerWidth < 1024) {
            requestAnimationFrame(() => contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
        }
    };
    const askQuestion = () => { setQuestionOpen(true); go("conversations"); };
    const newLabel = unreadQ === 1 ? "1 nova mensagem" : `${unreadQ} novas mensagens`;

    const navItems = [
        { key: "overview", label: "Visão geral", icon: Home },
        hasUpdates && { key: "updates", label: "Atualizações da obra", icon: Camera },
        questionsOn && { key: "conversations", label: "Conversas", icon: MessageCircle, badge: unreadQ, badgeLabel: newLabel },
        docsCount > 0 && { key: "documents", label: "Documentos", icon: FileText },
        { key: "contacts", label: "Contactos", icon: Users },
    ].filter(Boolean);
    const activeTab = navItems.some((i) => i.key === tab) ? tab : "overview";

    const cards = [
        hasUpdates && {
            key: "updates", tone: "green", icon: Camera, title: "Última atualização",
            value: updates[0] ? _relativeDayPT(updates[0]) : "Ainda sem atualizações",
            cta: "Ver atualizações", onClick: () => go("updates"),
        },
        questionsOn && {
            key: "conversations", tone: "copper", icon: MessageCircle, title: "Conversas",
            value: unreadQ > 0 ? newLabel : openQ === 0 ? "Nenhuma conversa em aberto" : openQ === 1 ? "1 conversa em aberto" : `${openQ} conversas em aberto`,
            cta: "Ver conversas", onClick: () => go("conversations"),
        },
        docsCount > 0 && {
            key: "documents", tone: "slate", icon: FileText, title: "Documentos",
            value: docsCount === 1 ? "1 documento disponível" : `${docsCount} documentos disponíveis`,
            cta: "Ver documentos", onClick: () => go("documents"),
        },
    ].filter(Boolean);

    return (
        <>
            <PageHead
                title="O seu pedido — O Homem da Obra"
                description="Acompanhe o seu pedido de remodelação"
                canonical={null}
            />
            <div className="min-h-screen bg-[hsl(var(--cream))]" data-testid="client-area">
                <header className="border-b border-stone-200/60 bg-white/40 backdrop-blur-sm">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="logo-mark text-2xl text-[hsl(var(--forest))]">H<span className="dot">.</span></span>
                            <span className="font-display font-bold text-sm text-[hsl(var(--forest))]">O Homem da Obra</span>
                        </div>
                        <div className="text-xs font-medium text-stone-500 hidden sm:block">Espaço privado</div>
                    </div>
                </header>

                <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-12 sm:pt-12 sm:pb-16 space-y-6 sm:space-y-8">
                    <ClientIntro name={d.name} />
                    <ClientHero d={d} photoId={heroPhoto} />
                    <ClientNav items={navItems} active={activeTab} onSelect={go} />

                    <div ref={contentRef} className="scroll-mt-4 space-y-6 sm:space-y-8" data-testid={`client-tab-${activeTab}`}>
                        {activeTab === "overview" && (
                            <>
                                <OverviewCards cards={cards} />
                                <ProposalSection data={d} token={token} onReload={reload} onAskQuestion={askQuestion} onOpenConversations={() => go("conversations")} />
                                <ClientNote text={d.client_note} />
                                <ClientTimeline d={d} />
                                <YourSide d={d} />
                            </>
                        )}
                        {activeTab === "updates" && <ExecutionSection d={d} />}
                        {activeTab === "conversations" && (
                            <ConversationsPanel token={token} conversations={conversations} composing={questionOpen} setComposing={setQuestionOpen} onReload={reload} />
                        )}
                        {activeTab === "documents" && <DocumentsPanel p={p} />}
                        {activeTab === "contacts" && <ContactsPanel settings={settings} />}
                    </div>
                </main>

                <footer className="border-t border-stone-200/60 py-8 px-4 sm:px-8 mt-4">
                    <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-stone-500">
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center">
                            {settings?.phone && (
                                <a href={`tel:${(settings.phone || "").replace(/\s+/g, "")}`} className="flex items-center gap-1.5 hover:text-[hsl(var(--forest))]" data-testid="client-footer-phone">
                                    <Phone className="w-3.5 h-3.5" /> {settings.phone}
                                </a>
                            )}
                            {settings?.email && (
                                <a href={`mailto:${settings.email}`} className="flex items-center gap-1.5 hover:text-[hsl(var(--forest))]" data-testid="client-footer-email">
                                    <Mail className="w-3.5 h-3.5" /> {settings.email}
                                </a>
                            )}
                        </div>
                        <div className="text-xs font-semibold tracking-wide">O Homem da Obra</div>
                    </div>
                </footer>
            </div>
        </>
    );
}

function ClientNote({ text }) {
    if (!text || !text.trim()) return null;
    return (
        <section className="bg-white rounded-[28px] border border-stone-200/80 p-6 sm:p-8 relative overflow-hidden" data-testid="client-note">
            <span className="absolute top-0 left-0 w-1 h-full bg-[hsl(var(--copper))]" aria-hidden />
            <div className="max-w-3xl pl-2">
                <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-4 h-4 text-[hsl(var(--copper))]" aria-hidden />
                    <span className={`${LABEL} text-stone-500`}>Uma nota da nossa parte</span>
                </div>
                <p className="text-stone-800 leading-relaxed whitespace-pre-wrap text-base">{text}</p>
            </div>
        </section>
    );
}

function ClientTimeline({ d }) {
    const steps = d.timeline_steps || [];
    return (
        <section className="bg-white rounded-[28px] border border-stone-200/80 p-6 sm:p-8">
            <h2 className="font-display text-lg font-bold text-[hsl(var(--forest))] mb-5">Como avançamos</h2>
            <ol className="space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:gap-1 sm:items-start sm:relative" data-testid="client-timeline">
                {steps.map((step, idx) => {
                    const n = idx + 1;
                    const isActive = n === d.timeline_step;
                    const isDone = n < d.timeline_step;
                    return (
                        <li key={step.key} className="flex items-center gap-3 sm:flex-col sm:items-center sm:text-center sm:flex-1 sm:relative" data-testid={`client-timeline-step-${n}`}>
                            {idx < (steps.length - 1) && (
                                <span className={`hidden sm:block absolute top-4 h-px ${isDone ? "bg-[hsl(var(--copper))]" : "bg-stone-200"}`} style={{ left: `${(idx + 0.5) * (100 / steps.length)}%`, width: `${100 / steps.length}%` }} aria-hidden />
                            )}
                            <span className={`relative z-10 flex items-center justify-center w-9 h-9 rounded-full text-xs font-bold flex-shrink-0 ${
                                isActive ? "bg-[hsl(var(--copper))] text-white ring-4 ring-[hsl(var(--copper))]/15" :
                                isDone ? "bg-[hsl(var(--forest))] text-white" :
                                "bg-white border border-stone-300 text-stone-400"
                            }`}>
                                {isDone ? <CheckCircle2 className="w-4 h-4" /> : n}
                            </span>
                            <span className={`text-[15px] sm:text-sm sm:mt-3 sm:max-w-[100px] ${
                                isActive ? "text-[hsl(var(--forest))] font-semibold" :
                                isDone ? "text-stone-600" : "text-stone-400"
                            }`}>
                                {step.label}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

function YourSide({ d }) {
    return (
        <section className="bg-white rounded-[28px] border border-stone-200/80 p-6 sm:p-8" data-testid="client-your-side">
            <h2 className="font-display text-lg font-bold text-[hsl(var(--forest))] mb-2">Da sua parte</h2>
            {d.proposal?.available ? (
                <p className="text-stone-800 text-base leading-relaxed max-w-3xl">
                    A sua proposta está pronta. Consulte os documentos acima e escolha uma das opções.
                </p>
            ) : d.client_action_required ? (
                <p className="text-stone-800 text-base leading-relaxed max-w-3xl">
                    Precisamos de alguma coisa de si. Vamos entrar em contacto brevemente.
                </p>
            ) : (
                <p className="text-stone-700 text-base leading-relaxed max-w-3xl">
                    Não precisa de fazer nada de momento. Se surgir algo importante, avisamos-lhe por email.
                </p>
            )}
        </section>
    );
}

function DocumentsPanel({ p }) {
    const onlyProposal = Object.values(p.files || {}).filter(Boolean).length === 1;
    return (
        <section className={CARD} data-testid="client-documents-section">
            <div className="max-w-2xl">
                <h2 className={H2}>Documentos</h2>
                <p className="text-stone-600 text-base leading-relaxed mt-2">
                    {onlyProposal ? "A proposta da sua obra. Toque no documento para o abrir." : "Os documentos da proposta da sua obra. Toque num documento para o abrir."}
                </p>
                {p.adjudicated_at && (
                    <p className="text-sm text-stone-500 mt-2" data-testid="client-documents-adjudicated">
                        Adjudicação confirmada em {formatDateTimePT(p.adjudicated_at)}.
                    </p>
                )}
                <div className="mt-6">
                    <DocumentsList files={p.files} readonly />
                </div>
            </div>
        </section>
    );
}


/* -------------------------------------------------------------------------
   Proposal section — 4 possible states:
   1. Not visible (no proposal_available && no adjudicated/declined) → null
   2. Available → show published docs + 3 CTAs; on "avançar" reveal one checkbox per published doc
   3. Adjudicated → show green success card + 3 docs (view-only)
   4. Declined → sober "encerrado" card
   "Tenho uma questão" abre uma nova conversa na secção Conversas.
   ------------------------------------------------------------------------- */
function ProposalSection({ data, token, onReload, onAskQuestion, onOpenConversations }) {
    const p = data.proposal || {};
    const [flow, setFlow] = useState("idle"); // idle | avancar
    const [checks, setChecks] = useState({ p: false, t: false, w: false });
    const [submitting, setSubmitting] = useState(false);

    // Reset local flow when server state changes (after a successful action)
    useEffect(() => {
        setFlow("idle");
        setChecks({ p: false, t: false, w: false });
    }, [p.adjudicated, p.declined, p.published_at]);

    if (!p.available && !p.adjudicated && !p.declined) return null;

    const files = p.files || {};
    const docN = Object.values(files).filter(Boolean).length;
    const allChecked = checks.p && (!files.terms || checks.t) && (!files.warranty || checks.w);

    const submit = async (type, extra = {}) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const { data: resp } = await api.post(`/public/cliente/${token}/action`, { type, ...extra });
            toast.success(resp.message || "Recebido.");
            await onReload();
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ocorreu um erro. Tente novamente.");
            return false;
        } finally { setSubmitting(false); }
    };

    // ============ ADJUDICATED ============
    if (p.adjudicated) {
        const plannedDate = formatLongDatePT(data.execution?.date);
        return (
            <section className="bg-white rounded-[28px] border border-emerald-200 p-6 sm:p-10 shadow-[0_1px_2px_rgba(24,53,47,0.04)]" data-testid="client-proposal-adjudicated">
                <div className="max-w-3xl">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                            <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <div className={`${LABEL} text-emerald-700 mb-1`}>Adjudicação confirmada</div>
                            <h2 className={H2}>Recebemos a sua confirmação.</h2>
                            {p.adjudicated_at && (
                                <p className="text-sm text-stone-600 mt-2">
                                    Confirmada em <strong className="text-[hsl(var(--forest))]">{formatDateTimePT(p.adjudicated_at)}</strong>.
                                </p>
                            )}
                        </div>
                    </div>
                    <p className="text-stone-700 text-base leading-relaxed mb-6" data-testid="client-adjudicated-text">
                        {plannedDate
                            ? `Obrigado pela sua confiança. A execução está prevista para ${plannedDate}. Se houver alguma alteração, entraremos em contacto consigo.`
                            : "Obrigado pela sua confiança. Entraremos em contacto consigo brevemente para agendar a execução da obra."}
                    </p>
                    <DocumentsList files={p.files} readonly />
                </div>
            </section>
        );
    }

    // ============ DECLINED ============
    if (p.declined) {
        return (
            <section className={CARD} data-testid="client-proposal-declined">
                <div className="max-w-3xl">
                    <div className={`${LABEL} text-stone-500 mb-3`}>Processo encerrado</div>
                    <h2 className={`${H2} mb-3`}>Ok, entendido.</h2>
                    <p className="text-stone-700 text-base leading-relaxed mb-4">
                        Registámos a sua decisão de não avançar. Se mudar de ideias, entre em contacto connosco a qualquer momento.
                    </p>
                    {p.declined_at && (
                        <p className="text-sm text-stone-500">Decidido em {formatDateTimePT(p.declined_at)}.</p>
                    )}
                </div>
            </section>
        );
    }

    // ============ AVAILABLE — main flow ============
    return (
        <section className={CARD} data-testid="client-proposal-section">
            <div className="max-w-3xl">
                <div className={`${LABEL} text-[hsl(var(--copper))] mb-3`}>A sua proposta</div>
                <h2 className={`${H2} mb-3`}>Está pronta para revisão.</h2>
                <p className="text-stone-700 text-base leading-relaxed mb-6">
                    {docN > 1 ? "Consulte os documentos abaixo." : "Consulte a proposta abaixo."} Quando estiver preparado, escolha uma das opções.
                </p>

                {p.has_doubt && (
                    <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="client-doubt-banner">
                        <HelpCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 text-sm">
                            <div className="text-amber-900 font-semibold">Recebemos a sua mensagem</div>
                            <p className="text-amber-800 mt-0.5">Vamos responder-lhe na secção Conversas, aqui na sua Área do Cliente.</p>
                            <button type="button" onClick={onOpenConversations} className="mt-2 inline-flex items-center gap-1.5 font-semibold text-amber-900 underline underline-offset-2 hover:no-underline" data-testid="client-doubt-open-conversations">
                                Ver conversas <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                <DocumentsList files={p.files} />

                {flow === "idle" && (
                    <div className="mt-8 pt-6 border-t border-stone-100" data-testid="client-actions">
                        <div className="text-base font-semibold text-[hsl(var(--forest))] mb-4">O que gostaria de fazer?</div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                onClick={() => setFlow("avancar")}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[hsl(var(--copper))] text-white text-base font-semibold hover:opacity-90 transition-opacity"
                                data-testid="client-action-avancar"
                            >
                                Quero avançar
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={onAskQuestion}
                                disabled={submitting}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-stone-300 text-stone-700 text-base font-semibold hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition-colors disabled:opacity-50"
                                data-testid="client-action-questao"
                            >
                                Tenho uma questão
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!window.confirm("Confirma que não pretende avançar com esta proposta?")) return;
                                    submit("nao_avancar");
                                }}
                                disabled={submitting}
                                className="flex-1 text-center px-6 py-4 text-stone-500 text-base hover:text-stone-800 transition-colors disabled:opacity-50"
                                data-testid="client-action-nao-avancar"
                            >
                                Não pretendo avançar
                            </button>
                        </div>
                    </div>
                )}

                {/* Adjudicação flow — one checkbox per published doc */}
                {flow === "avancar" && (
                    <div className="mt-8 pt-6 border-t border-stone-100 space-y-5" data-testid="client-adjudicacao-flow">
                        <div>
                            <div className="text-base font-semibold text-[hsl(var(--forest))] mb-1">Confirmação da adjudicação</div>
                            <p className="text-sm text-stone-600 leading-relaxed">
                                Antes de confirmar, consulte cada documento e assinale as confirmações abaixo.
                            </p>
                        </div>
                        <div className="space-y-3">
                            <CheckRow
                                label="Li e aceito a proposta apresentada."
                                checked={checks.p}
                                onChange={(v) => setChecks((c) => ({ ...c, p: v }))}
                                testid="client-adjudicacao-check-p"
                            />
                            {files.terms && (
                                <CheckRow
                                    label="Li e aceito as Condições Gerais e Particulares."
                                    checked={checks.t}
                                    onChange={(v) => setChecks((c) => ({ ...c, t: v }))}
                                    testid="client-adjudicacao-check-t"
                                />
                            )}
                            {files.warranty && (
                                <CheckRow
                                    label="Li e tomei conhecimento das garantias aplicáveis."
                                    checked={checks.w}
                                    onChange={(v) => setChecks((c) => ({ ...c, w: v }))}
                                    testid="client-adjudicacao-check-w"
                                />
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => submit("adjudicar", { checks })}
                                disabled={!allChecked || submitting}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[hsl(var(--forest))] text-white text-base font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="client-adjudicacao-confirm"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                Confirmar adjudicação
                            </button>
                            <button
                                type="button"
                                onClick={() => setFlow("idle")}
                                className="flex-1 sm:flex-initial px-6 py-4 text-stone-500 text-base hover:text-stone-800"
                                data-testid="client-adjudicacao-cancel"
                            >
                                Voltar atrás
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function DocumentsList({ files, readonly }) {
    if (!files) return null;
    const items = [
        { key: "proposal", label: "Proposta / Orçamento", file: files.proposal },
        { key: "terms", label: "Condições Gerais e Particulares", file: files.terms },
        { key: "warranty", label: "Garantias", file: files.warranty },
    ].filter((it) => it.file);
    return (
        <div className="space-y-2.5" data-testid="client-docs" data-readonly={readonly ? "true" : undefined}>
            {items.map((it) => (
                <a
                    key={it.key}
                    href={it.file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-4 border border-stone-200 rounded-2xl p-4 min-h-[68px] hover:border-[hsl(var(--copper))] hover:bg-[hsl(var(--cream))]/40 transition-colors group"
                    data-testid={`client-doc-${it.key}`}
                >
                    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center group-hover:bg-[hsl(var(--copper))] group-hover:text-white transition-colors">
                        <FileText className="w-5 h-5" aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-base font-semibold text-stone-900 leading-snug">{it.label}</div>
                        <div className="text-sm text-stone-500 truncate">{it.file.filename || "documento.pdf"}</div>
                    </div>
                    <FileDown className="w-5 h-5 text-stone-400 group-hover:text-[hsl(var(--copper))] flex-shrink-0" aria-hidden />
                </a>
            ))}
        </div>
    );
}

function CheckRow({ label, checked, onChange, testid }) {
    return (
        <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${checked ? "border-[hsl(var(--copper))] bg-[hsl(var(--cream))]/50" : "border-stone-200 hover:border-stone-300"}`} data-testid={testid}>
            <span className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-colors ${checked ? "bg-[hsl(var(--copper))] border-[hsl(var(--copper))]" : "bg-white border-stone-300"}`}>
                {checked && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
            </span>
            <span className="text-[15px] text-stone-800 leading-relaxed">
                {label}
            </span>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
        </label>
    );
}

/* -------------------------------------------------------------------------
   Bloco 3A — Execução, atualizações e conclusão
   Agrupamento por semana é apresentação (não persistido).
   ------------------------------------------------------------------------- */
const WEEKDAYS_PT = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];

function _startOfWeek(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function _weekLabelPT(wk, nowWk) {
    const diffDays = Math.round((nowWk - wk) / (24 * 3600 * 1000));
    const end = new Date(wk); end.setDate(end.getDate() + 6);
    const sameMonth = wk.getMonth() === end.getMonth();
    const d1 = wk.getDate(), d2 = end.getDate();
    const m1 = MONTHS_PT[wk.getMonth()];
    const m2 = MONTHS_PT[end.getMonth()];
    const range = sameMonth ? `${d1}–${d2} ${m1}` : `${d1} ${m1}–${d2} ${m2}`;
    if (diffDays === 0) return `Esta semana · ${range}`;
    if (diffDays === 7) return `Semana anterior · ${range}`;
    return range;
}

// Data da atualização (update_date, YYYY-MM-DD) como data local; fallback created_at.
function _effDay(u) {
    const [y, m, d] = String(u.update_date || "").split("-").map((n) => parseInt(n, 10));
    if (y && m && d) return new Date(y, m - 1, d);
    const c = new Date(u.created_at);
    c.setHours(0, 0, 0, 0);
    return c;
}

function _formatDayPT(u) {
    const d = _effDay(u);
    return `${WEEKDAYS_PT[d.getDay()]}, ${d.getDate()} de ${MONTHS_PT[d.getMonth()]} de ${d.getFullYear()}`;
}

function _relativeDayPT(u) {
    const day = _effDay(u);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - day) / (24 * 3600 * 1000));
    const created = new Date(u.created_at);
    const time = created.toDateString() === day.toDateString()
        ? `, ${created.getHours().toString().padStart(2, "0")}:${created.getMinutes().toString().padStart(2, "0")}`
        : "";
    if (diffDays <= 0) return `Hoje${time}`;
    if (diffDays === 1) return `Ontem${time}`;
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return _formatDayPT(u);
}

function _groupByWeek(updates) {
    const groups = [];
    const nowWk = _startOfWeek(new Date());
    for (const u of updates) {
        const wk = _startOfWeek(_effDay(u));
        if (!groups.length || groups[groups.length - 1].wkKey !== wk.getTime()) {
            groups.push({ wkKey: wk.getTime(), label: _weekLabelPT(wk, nowWk), items: [u] });
        } else {
            groups[groups.length - 1].items.push(u);
        }
    }
    return groups;
}

function ExecutionSection({ d }) {
    const e = d.execution || {};
    const isExecucao = d.status === "Execução";
    const isConcluido = d.status === "Concluído";
    const [lightbox, setLightbox] = useState(null);
    if (!isExecucao && !isConcluido) return null;

    const updates = Array.isArray(e.updates) ? e.updates : [];
    const empty = updates.length === 0;
    const last = updates[0];
    const nowWk = _startOfWeek(new Date());
    const thisWeek = updates.filter((u) => _startOfWeek(_effDay(u)).getTime() === nowWk.getTime());
    const thisWeekPhotos = thisWeek.reduce((n, u) => n + ((u.photos || []).length), 0);
    const groups = _groupByWeek(updates);

    // Flatten all photos across updates to enable swipe through the whole obra
    const allPhotos = updates.flatMap((u) => (u.photos || []).map((p) => ({ ...p, update_id: u.id })));
    const openLightbox = (updateId, idxInUpdate) => {
        let globalIdx = 0;
        for (const u of updates) {
            if (u.id === updateId) {
                globalIdx += idxInUpdate;
                break;
            }
            globalIdx += (u.photos || []).length;
        }
        setLightbox({ photos: allPhotos, index: globalIdx });
    };

    return (
        <section className={CARD} data-testid="client-execution-section">
            <div className="max-w-3xl">
                <h2 className={H2}>Atualizações da obra</h2>

                {empty && isExecucao ? (
                    <p className="text-stone-700 text-base leading-relaxed mt-3" data-testid="execution-empty">
                        Execução em curso. A primeira atualização será publicada em breve.
                    </p>
                ) : (
                    <>
                        {last && (
                            <div className="mt-6 rounded-2xl bg-[hsl(var(--cream))] p-5 sm:p-6 space-y-4" data-testid="execution-summary">
                                <div>
                                    <div className="text-sm text-stone-500 mb-0.5">Última atualização</div>
                                    <div className="font-display text-2xl sm:text-3xl font-bold text-[hsl(var(--forest))] tracking-tight" data-testid="execution-last-time">
                                        {_relativeDayPT(last)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-x-3 flex-wrap text-[15px] text-stone-700" data-testid="execution-week-stats">
                                    <span>Esta semana: <strong className="text-[hsl(var(--forest))]">{thisWeek.length}</strong> atualização{thisWeek.length === 1 ? "" : "s"}</span>
                                    {thisWeekPhotos > 0 && <span className="text-stone-400" aria-hidden>·</span>}
                                    {thisWeekPhotos > 0 && (
                                        <span><strong className="text-[hsl(var(--forest))]">{thisWeekPhotos}</strong> fotografia{thisWeekPhotos === 1 ? "" : "s"}</span>
                                    )}
                                </div>
                                {last.next_step && isExecucao && (
                                    <div className="pt-4 border-t border-stone-200/70">
                                        <div className="text-sm font-semibold text-[hsl(var(--copper))] mb-1">A seguir</div>
                                        <p className="text-stone-800 text-base leading-relaxed" data-testid="execution-next">
                                            {last.next_step}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-10 mt-8" data-testid="execution-history">
                            {groups.map((grp) => (
                                <div key={grp.wkKey}>
                                    <div className="text-sm font-semibold text-stone-500 mb-5 pb-2 border-b border-stone-100">
                                        {grp.label}
                                    </div>
                                    <div className="space-y-8">
                                        {grp.items.map((u) => (
                                            <article key={u.id} className="pl-4 sm:pl-5 border-l-2 border-[hsl(var(--copper))]/30" data-testid={`execution-update-${u.id}`}>
                                                <div className="text-sm text-stone-600 font-medium mb-3 first-letter:uppercase" data-testid={`execution-update-date-${u.id}`}>
                                                    {_formatDayPT(u)}
                                                </div>
                                                {u.photos && u.photos.length > 0 && (
                                                    <PhotoGrid photos={u.photos} onOpen={(i) => openLightbox(u.id, i)} />
                                                )}
                                                <div className="mt-4">
                                                    <div className="text-sm font-semibold text-[hsl(var(--forest))] mb-1">O que fizemos</div>
                                                    <p className="text-stone-800 text-base leading-relaxed whitespace-pre-wrap" data-testid="update-what">{u.what_we_did}</p>
                                                </div>
                                                {u.next_step && (
                                                    <div className="mt-4">
                                                        <div className="text-sm font-semibold text-[hsl(var(--forest))] mb-1">O que acontece a seguir</div>
                                                        <p className="text-stone-800 text-base leading-relaxed whitespace-pre-wrap" data-testid="update-next">{u.next_step}</p>
                                                    </div>
                                                )}
                                                {u.note && (
                                                    <p className="mt-3 text-sm text-stone-600 italic leading-relaxed">
                                                        {u.note}
                                                    </p>
                                                )}
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            {lightbox && (
                <PhotoLightbox
                    photos={lightbox.photos}
                    startIndex={lightbox.index}
                    onClose={() => setLightbox(null)}
                />
            )}
        </section>
    );
}

function PhotoGrid({ photos, onOpen }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="update-photos">
            {photos.map((p, i) => (
                <button
                    key={p.file_id}
                    type="button"
                    onClick={() => onOpen && onOpen(i)}
                    className="block overflow-hidden rounded-xl bg-stone-100 aspect-square hover:ring-2 hover:ring-[hsl(var(--copper))] transition-shadow cursor-zoom-in p-0 border-0"
                    data-testid={`update-photo-${p.file_id}`}
                    aria-label={`Abrir fotografia ${i + 1}`}
                >
                    <img
                        src={`/api/files/${p.file_id}`}
                        alt={`Fotografia ${i + 1}`}
                        className="w-full h-full object-cover select-none pointer-events-none"
                        loading="lazy"
                        draggable={false}
                    />
                </button>
            ))}
        </div>
    );
}

/* -------------------------------------------------------------------------
   PhotoLightbox — fullscreen fotos com swipe horizontal (mobile), setas
   (desktop) e teclas ESC/←/→. Tap-outside (ou botão X) fecha. Lock scroll
   do body enquanto aberto. Zero libs externas.
   ------------------------------------------------------------------------- */
function PhotoLightbox({ photos, startIndex, onClose }) {
    const total = photos.length;
    const [index, setIndex] = useState(startIndex || 0);
    const [touchStartX, setTouchStartX] = useState(null);

    const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
    const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose, next, prev]);

    const current = photos[index];
    if (!current) return null;

    const onTouchStart = (e) => setTouchStartX(e.changedTouches[0].clientX);
    const onTouchEnd = (e) => {
        if (touchStartX == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 50 && total > 1) {
            if (dx > 0) prev();
            else next();
        }
        setTouchStartX(null);
    };

    return (
        <div
            className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4"
            onClick={onClose}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="photo-lightbox"
            role="dialog"
            aria-modal="true"
        >
            <button
                type="button"
                onClick={(ev) => { ev.stopPropagation(); onClose(); }}
                className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition"
                data-testid="lightbox-close"
                aria-label="Fechar"
            >
                <XCircle className="w-5 h-5" />
            </button>
            {total > 1 && (
                <>
                    <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); prev(); }}
                        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 text-white items-center justify-center hover:bg-white/20 transition"
                        data-testid="lightbox-prev"
                        aria-label="Anterior"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); next(); }}
                        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 text-white items-center justify-center hover:bg-white/20 transition"
                        data-testid="lightbox-next"
                        aria-label="Próxima"
                    >
                        <ArrowRight className="w-5 h-5" />
                    </button>
                </>
            )}
            <img
                src={`/api/files/${current.file_id}`}
                alt={`Fotografia ${index + 1} de ${total}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg select-none"
                onClick={(ev) => ev.stopPropagation()}
                draggable={false}
            />
            {total > 1 && (
                <div
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm font-mono"
                    data-testid="lightbox-counter"
                >
                    {index + 1} / {total}
                </div>
            )}
        </div>
    );
}
